import React from "react";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "@posthog/react";
import { withRouter } from "react-router-dom";
import { identifyUser, identifyOrganization, resetUser } from "@shared/lib/posthog";

// Pageview tracker - uses withRouter to listen to React Router v4 location changes
function PageviewTrackerInner({ location, children }: { location: any; children: React.ReactNode }) {
  const previousPathnameRef = React.useRef<string>("");

  React.useEffect(() => {
    if (location?.pathname && location.pathname !== previousPathnameRef.current) {
      previousPathnameRef.current = location.pathname;
      if (posthog.__loaded) {
        posthog.capture("$pageview");
      }
    }
  }, [location.pathname]);

  return <>{children}</>;
}

const PageviewTracker = withRouter(PageviewTrackerInner as any) as any;

// Main provider - initializes PostHog and wraps children with pageview tracking
function PostHogProvider({ children }: { children: React.ReactNode }) {
  const apiKey = (window as any).POSTHOG_API_KEY;
  const host = (window as any).POSTHOG_BROWSER_HOST;

  React.useEffect(() => {
    if (!apiKey || (window as any).IS_TEST_ENV) return;

    posthog.init(apiKey, {
      api_host: host || "https://us.i.posthog.com",
      capture_pageview: false, // We handle pageviews manually via React Router
      capture_pageleave: true,
      loaded: (ph) => {
        ph.capture("$pageview");
        if ((window as any).IS_DEVELOPMENT) {
          ph.debug();
        }
      },
    });
  }, []);

  return (
    <PHProvider client={posthog}>
      <PageviewTracker>
        {children}
      </PageviewTracker>
    </PHProvider>
  );
}

export { PostHogProvider, identifyUser, identifyOrganization, resetUser };
